import React from 'react'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'

// Doctor uploads a short teaching video explaining a medication (usage + expected
// side effects). Gemini extracts structured knowledge that grounds the voice
// agent's medication-feedback calls. The library grows one video at a time.
export default function MedicationTraining() {
  const { fetchWithAuth } = useAuth()
  const [meds, setMeds] = React.useState([])
  const [busy, setBusy] = React.useState(false)
  const [msg, setMsg] = React.useState('')
  const [err, setErr] = React.useState('')
  const fileRef = React.useRef(null)

  const load = React.useCallback(() => {
    fetchWithAuth(`${API_BASE}/api/doctor/medications`).then(r => r.json())
      .then(d => setMeds(d.medications || [])).catch(() => {})
  }, [fetchWithAuth])
  React.useEffect(() => { load() }, [load])

  const upload = async (file) => {
    if (!file) return
    setErr(''); setMsg(''); setBusy(true)
    try {
      const fd = new FormData()
      fd.append('video', file)
      const res = await fetchWithAuth(`${API_BASE}/api/doctor/medication-video`, { method: 'POST', body: fd })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.message || 'Upload failed')
      setMsg(`✓ Learned ${data.count} medication${data.count === 1 ? '' : 's'} from the video.`)
      load()
    } catch (e) { setErr(e.message) }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = '' }
  }

  const C = { g: '#0f5132', line: '#e2e8e5', ink: '#0f172a', mut: '#5a7065', soft: '#f0f7f3' }
  const card = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 14, padding: 16, marginBottom: 16 }

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ ...card, background: C.soft }}>
        <div style={{ fontWeight: 800, fontSize: '1.05rem', color: C.g, marginBottom: 4 }}>🎙️ Train medication knowledge</div>
        <p style={{ fontSize: '.86rem', color: C.mut, margin: '0 0 12px' }}>
          Record a short audio or video explaining a medication — how to use it and what side effects to expect.
          The assistant learns from it and uses it on patient follow-up calls.
        </p>
        <input ref={fileRef} type="file" accept="audio/*,video/*" hidden onChange={e => upload(e.target.files[0])} />
        <button onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}
          style={{ padding: '.7rem 1.4rem', borderRadius: 10, border: 'none', background: busy ? '#9cc4ae' : C.g, color: '#fff', fontWeight: 700, fontSize: '.9rem', cursor: busy ? 'not-allowed' : 'pointer' }}>
          {busy ? 'Learning from recording…' : '＋ Upload teaching audio / video'}
        </button>
        {msg && <p style={{ fontSize: '.85rem', color: C.g, marginTop: 10 }}>{msg}</p>}
        {err && <p style={{ fontSize: '.85rem', color: '#c0392b', marginTop: 10 }}>{err}</p>}
      </div>

      <div style={{ fontWeight: 700, fontSize: '.9rem', color: C.ink, margin: '4px 0 10px' }}>
        Medication library {meds.length ? `(${meds.length})` : ''}
      </div>
      {meds.length === 0 && <p style={{ fontSize: '.85rem', color: C.mut }}>No medications learned yet. Upload a video to start.</p>}
      {meds.map((m, i) => (
        <div key={i} style={card}>
          <div style={{ fontWeight: 700, color: C.ink }}>{m.displayName || m.name}{m.brands && m.brands.length ? <span style={{ color: C.mut, fontWeight: 400 }}> · {m.brands.join(', ')}</span> : null}</div>
          {m.category && <div style={{ fontSize: '.78rem', color: C.mut, marginBottom: 6 }}>{m.category}</div>}
          {m.usage && <div style={{ fontSize: '.85rem', margin: '4px 0' }}><b>Usage:</b> {m.usage}</div>}
          {m.expectedSideEffects && m.expectedSideEffects.length > 0 && (
            <div style={{ fontSize: '.85rem', margin: '4px 0' }}><b>Expected side effects:</b> {m.expectedSideEffects.join(', ')}</div>
          )}
          {m.whatToAsk && <div style={{ fontSize: '.85rem', margin: '4px 0', color: C.mut }}><b>Ask on follow-up:</b> {m.whatToAsk}</div>}
          {m.flagIf && <div style={{ fontSize: '.83rem', margin: '4px 0', color: '#B7770D' }}><b>Flag if:</b> {m.flagIf}</div>}
        </div>
      ))}
    </div>
  )
}
